import React from "react";
import { useNavigate } from "react-router-dom";
import { FaSlidersH } from "react-icons/fa";
import "../styles/propertyListing.css";

import useScrollRestore from "../hooks/useScrollRestore";
import usePropertyListing from "../hooks/usePropertyListing";
import { useFavorites } from "../Components/Context/FavoritesContext";

import PropertyListingHeader from "../Components/properties/PropertyListingHeader";
import PropertyFilters from "../Components/properties/PropertyFilters";
import PropertyGrid from "../Components/properties/PropertyGrid";
import LoadMoreButton from "../Components/properties/LoadMoreButton";

const PAGE_KEY = "pesquisa";
const pageFilter = () => true;

function buildSummaryParts(criteria) {
  if (!criteria) return [];
  const parts = [];

  if (criteria.categorias?.length) parts.push(criteria.categorias.join(", "));
  if (criteria.cidades?.length) parts.push(criteria.cidades.join(", "));
  if (criteria.bairros?.length) parts.push(criteria.bairros.join(", "));
  if (criteria.quartos && criteria.quartos !== "Qualquer") {
    parts.push(`${criteria.quartos}+ quartos`);
  }
  if (criteria.banheiros && criteria.banheiros !== "Qualquer") {
    parts.push(`${criteria.banheiros}+ banheiros`);
  }
  if (criteria.vagas && criteria.vagas !== "Qualquer") {
    parts.push(`${criteria.vagas}+ vagas`);
  }
  if (criteria.comodidades?.length) parts.push(criteria.comodidades.join(", "));
  if (criteria.codigo?.trim()) parts.push(`Cód. ${criteria.codigo}`);
  parts.push(criteria.modality === "alugar" ? "Aluguel" : "Venda");

  return parts;
}

function Pesquisa() {
  const navigate = useNavigate();
  const {
    favorites,
    addFavorite,
    removeFavorite,
    filters,
    searchApplied,
    clearSearch,
    setIsFilterModalOpen,
  } = useFavorites();

  const criteria = searchApplied ? filters?.criteria : null;

  const listing = usePropertyListing({
    pageKey: PAGE_KEY,
    pageFilter,
    mergeFilterResults: true,
    enableModality: true,
    lockModality: true,
    defaultModality: criteria?.modality,
  });

  const saveScroll = useScrollRestore(
    `scroll-${PAGE_KEY}`,
    !listing.loading
  );

  const toggleFavorite = (property) => {
    if (
      favorites.some(
        (fav) => fav.imovelInfo?.codigo === property.imovelInfo?.codigo
      )
    ) {
      removeFavorite(property.imovelInfo?.codigo);
    } else {
      addFavorite(property);
    }
  };

  const handleNavigate = (codigo) => {
    saveScroll();
    navigate(`/imovel/${codigo}`);
  };

  const openEditSearch = () => setIsFilterModalOpen(true);

  const handleClearSearch = () => {
    clearSearch();
    navigate("/todos-imoveis");
  };

  const summaryParts = buildSummaryParts(criteria);

  return (
    <div className="listing-page">
      <PropertyListingHeader
        backLabel="Resultado da pesquisa"
        title="Imóveis encontrados para você"
        subtitle={
          criteria
            ? `Encontramos ${listing.filteredProperties.length} imóveis com os critérios selecionados.`
            : "Explore os imóveis disponíveis ou refine sua busca."
        }
      />

      <PropertyFilters
        sort={listing.sort}
        onSortChange={listing.setSort}
        city={listing.city}
        onCityChange={listing.setCity}
        cityOptions={listing.cityOptions}
        neighborhood={listing.neighborhood}
        onNeighborhoodChange={listing.setNeighborhood}
        neighborhoodOptions={listing.neighborhoodOptions}
        hasActiveFilters={listing.hasActiveFilters}
        onClear={listing.clearFilters}
      />

      {!listing.loading && (
        <div className="listing-search-summary">
          <p className="listing-counter">
            <strong>{listing.filteredProperties.length}</strong> imóveis
            encontrados
          </p>
          {summaryParts.length > 0 && (
            <p className="listing-search-summary__chips">
              {summaryParts.join(" · ")}
            </p>
          )}
          {criteria && (
            <div className="listing-search-summary__actions">
              <button
                type="button"
                className="listing-search-summary__edit"
                onClick={openEditSearch}
              >
                <FaSlidersH aria-hidden="true" /> Editar busca
              </button>
              <button
                type="button"
                className="listing-search-summary__clear"
                onClick={handleClearSearch}
              >
                Limpar busca
              </button>
            </div>
          )}
        </div>
      )}

      <PropertyGrid
        properties={listing.visibleProperties}
        loading={listing.loading}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        onNavigate={handleNavigate}
        hasActiveFilters={listing.hasActiveFilters}
        onClearFilters={listing.clearFilters}
        emptyDescription={
          criteria
            ? "Não encontramos imóveis com esses critérios."
            : "Não encontramos imóveis com esses filtros."
        }
        emptyActions={
          criteria ? (
            <>
              <button type="button" onClick={openEditSearch}>
                Editar busca
              </button>
              <button
                type="button"
                className="listing-empty__action--secondary"
                onClick={handleClearSearch}
              >
                Limpar busca
              </button>
            </>
          ) : undefined
        }
      />

      <LoadMoreButton
        onClick={listing.loadMore}
        shown={listing.visibleProperties.length}
        total={listing.filteredProperties.length}
      />
    </div>
  );
}

export default Pesquisa;
